<div *ngIf="showAddButton" class="flex justify-between items-center mb-4">
  <h3 class="text-lg font-semibold">{{ 'BTB.DASHBOARD.PARTNER_DETAIL.INVOICES.TITLE' | translate }}</h3>
  <button
    mat-raised-button
    color="primary"
    (click)="onAddInvoice()"
    class="bg-red-600 text-white hover:bg-red-700">
    <mat-icon class="mr-2">add</mat-icon>
    {{ 'BTB.DASHBOARD.PARTNER_DETAIL.INVOICES.ADD_INVOICE' | translate }}
  </button>
</div>

<!-- Billing Summary -->
<div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 w-full mb-6">
  <div class="flex flex-col md:flex-row md:items-center md:justify-center gap-8">
    <div class="flex flex-col items-center">
      <div class="text-blue-600 text-sm font-medium mb-1">{{ 'BTB.DASHBOARD.PENDING_TO_BILL' | translate }}</div>
      <div class="text-2xl font-bold text-blue-800">{{ pendingTotal | currency:'EUR' }}</div>
    </div>
    <div class="flex flex-col items-center">
      <div class="text-green-600 text-sm font-medium mb-1">{{ 'BTB.DASHBOARD.TOTAL_BILLING' | translate }}</div>
      <div class="text-2xl font-bold text-green-800">{{ totalBilling | currency:'EUR' }}</div>
    </div>
  </div>
</div>

<!-- All Invoices -->
<div class="dashboard-box p-4 w-full">
  <app-generic-list class="w-full flex-col"
                    [items]="allInvoices"
                    [filterFields]="filterFields"
                    [showFilters]="true"
                    [isFiltersCollapsed]="true"
                    (filteredCount)="onFilteredCountChange('allInvoices', $event)">
    <ng-template #tableHeaderTemplate>
      <div class="flex justify-between items-center py-2 border-b font-semibold text-gray-600">
        <span class="w-[200px]">{{ 'BTB.DASHBOARD.TABLE.NUMBER' | translate }}</span>
        <span class="w-32">{{ 'BTB.DASHBOARD.TABLE.CREATED_AT' | translate }}</span>
        <span class="w-32">{{ 'BTB.DASHBOARD.TABLE.START_DATE' | translate }}</span>
        <span class="w-32">{{ 'BTB.DASHBOARD.TABLE.END_DATE' | translate }}</span>
        <span class="w-28">{{ 'BTB.DASHBOARD.TABLE.AMOUNT' | translate }}</span>
        <span class="flex-1">{{ 'BTB.DASHBOARD.TABLE.STATUS' | translate }}</span>
        <span class="w-20 text-center">
          {{ 'BTB.DASHBOARD.TABLE.ACTIONS' | translate }}
        </span>
      </div>
    </ng-template>
    <ng-template #itemTemplate let-item>
      <div class="flex justify-between items-center py-2 border-b">
        <span class="w-[200px]">{{ item.id }}</span>
        <span class="w-32">{{ item.createdAt | date:'short' }}</span>
        <span class="w-32">{{ item.start_date | date:'short' }}</span>
        <span class="w-32">{{ item.end_date | date:'short' }}</span>
        <span class="w-28">{{ item.amount | currency:'EUR' }}</span>
        <span class="flex-1">
          <span *ngIf="!isProcessing(item.id!)" class="inline-flex px-2 py-1 rounded-full text-xs font-medium min-h-[28px] items-center justify-center text-center" [class]="item.status | invoiceStatusBadge">
            {{ 'BTB.DASHBOARD.INVOICE_STATUS.' + item.status | translate }}
          </span>
          <span *ngIf="isProcessing(item.id!)" class="inline-flex px-2 py-1 rounded-full text-xs font-medium min-h-[28px] items-center justify-center text-center bg-gray-100 text-gray-600 border border-gray-200">
            <mat-spinner diameter="16" strokeWidth="2" class="mr-2"></mat-spinner>
            {{ 'BTB.DASHBOARD.UPDATING' | translate }}
          </span>
        </span>
        <span class="w-20 flex justify-center">
          <button mat-icon-button [matMenuTriggerFor]="invoiceMenu" (click)="$event.stopPropagation()" [disabled]="!canPerformAction()">
            <mat-icon *ngIf="!isProcessing(item.id!)">more_vert</mat-icon>
            <mat-spinner *ngIf="isProcessing(item.id!)" diameter="24"></mat-spinner>
          </button>
          <mat-menu #invoiceMenu="matMenu">
            <button mat-menu-item (click)="onInvoiceAction('download', item)">
              <mat-icon>download</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.DOWNLOAD' | translate }}</span>
            </button>
            
            <!-- Actions for SETTLED invoices -->
            <button *ngIf="isBackoffice && item.status === 'SETTLED'" mat-menu-item (click)="onInvoiceAction('mark_as_pending', item)">
              <mat-icon>pending</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.MARK_AS_PENDING' | translate }}</span>
            </button>
            <button *ngIf="isBackoffice && item.status === 'SETTLED'" mat-menu-item (click)="onInvoiceAction('cancel_invoice', item)">
              <mat-icon>cancel</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.CANCEL_INVOICE' | translate }}</span>
            </button>
            
            <!-- Actions for PENDING invoices (BTB Dashboard) -->
            <button *ngIf="!isBackoffice && item.status === 'PENDING'" mat-menu-item (click)="onInvoiceAction('confirm_invoice', item)">
              <mat-icon>check_circle</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.CONFIRM_INVOICE' | translate }}</span>
            </button>
            <button *ngIf="!isBackoffice && item.status === 'PENDING'" mat-menu-item (click)="onInvoiceAction('cancel_invoice', item)">
              <mat-icon>cancel</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.CANCEL_INVOICE' | translate }}</span>
            </button>
            
            <!-- Actions for PENDING/PENDING_CONFIRMED invoices (Backoffice) -->
            <button *ngIf="isBackoffice && (item.status === 'PENDING' || item.status === 'PENDING_CONFIRMED')" mat-menu-item (click)="onInvoiceAction('mark_as_settled', item)">
              <mat-icon>check_circle</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.MARK_AS_SETTLED' | translate }}</span>
            </button>
            <button *ngIf="isBackoffice && (item.status === 'PENDING' || item.status === 'PENDING_CONFIRMED')" mat-menu-item (click)="onInvoiceAction('cancel_invoice', item)">
              <mat-icon>cancel</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.CANCEL_INVOICE' | translate }}</span>
            </button>
            
            <!-- Actions for CANCELLED invoices (Backoffice) -->
            <button *ngIf="isBackoffice && item.status === 'CANCELLED'" mat-menu-item (click)="onInvoiceAction('mark_as_pending', item)">
              <mat-icon>pending</mat-icon>
              <span>{{ 'BTB.DASHBOARD.ACTIONS.MARK_AS_PENDING' | translate }}</span>
            </button>
          </mat-menu>
        </span>
      </div>
    </ng-template>
    <div *ngIf="allInvoices.length === 0" class="text-gray-400 text-center mt-2">
      {{ 'BTB.DASHBOARD.NO_INVOICES' | translate }}
    </div>
  </app-generic-list>
</div>

<!-- Confirmation Modal -->
<div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" *ngIf="showConfirmationModal">
  <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
    <div class="flex items-center mb-4">
      <mat-icon class="text-yellow-500 mr-3">warning</mat-icon>
      <h3 class="text-lg font-semibold text-gray-900">
        {{ 'BTB.DASHBOARD.CONFIRM_STATUS_CHANGE' | translate }}
      </h3>
    </div>
    
    <div class="mb-6">
      <p class="text-gray-600 mb-3">
        {{ 'BTB.DASHBOARD.CONFIRM_STATUS_CHANGE_MESSAGE' | translate }}
      </p>
      
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex justify-between items-center mb-3">
          <span class="font-medium text-gray-700">{{ 'BTB.DASHBOARD.INVOICE_NUMBER' | translate }}:</span>
          <span class="text-gray-900 font-semibold">{{ pendingAction?.invoice?.id }}</span>
        </div>
        
        <div class="flex justify-between items-center mb-3">
          <span class="font-medium text-gray-700">{{ 'BTB.DASHBOARD.TABLE.AMOUNT' | translate }}:</span>
          <span class="text-gray-900 font-semibold">{{ pendingAction?.invoice?.amount | currency:'EUR' }}</span>
        </div>
        
        <div class="border-t pt-3">
          <div class="text-center">
            <span class="text-sm font-medium text-gray-600 mb-2 block">{{ 'BTB.DASHBOARD.STATUS_CHANGE' | translate }}:</span>
            <div class="flex items-center justify-center space-x-4">
              <span class="inline-flex px-3 py-1 rounded-full text-sm font-medium" [class]="(pendingAction?.invoice?.status || '') | invoiceStatusBadge">
                {{ 'BTB.DASHBOARD.INVOICE_STATUS.' + (pendingAction?.invoice?.status || '') | translate }}
              </span>
              <mat-icon class="text-gray-600 text-xl">arrow_forward</mat-icon>
              <span class="inline-flex px-3 py-1 rounded-full text-sm font-medium" [class]="getNewStatus() | invoiceStatusBadge">
                {{ 'BTB.DASHBOARD.INVOICE_STATUS.' + getNewStatus() | translate }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="flex justify-end space-x-3">
      <button 
        mat-button 
        (click)="cancelStatusChange()"
        class="text-gray-600 hover:text-gray-800">
        {{ 'BTB.DASHBOARD.CANCEL' | translate }}
      </button>
      <button 
        mat-raised-button 
        color="primary"
        (click)="confirmStatusChange()"
        class="bg-red-600 text-white hover:bg-red-700">
        {{ 'BTB.DASHBOARD.CONFIRM' | translate }}
      </button>
    </div>
  </div>
</div>
